import axios, { AxiosResponse } from "axios";

export const STUDIO_PLUS_PAYMENTS_PROVIDER = "StudioPlusPayments";
export const STUDIO_PLUS_PAYMENT_COMPLETE_EVENT = "payment-complete";
export const STUDIO_PLUS_PAYMENT_METHODS_UPDATED_EVENT =
  "payment-methods-updated";

const TERMINAL_PAYMENT_STATUSES = ["succeeded", "failed", "canceled", "cancelled"];
const STUDIO_PLUS_CHECKOUT_RETRY_QUERY_PARAM = "paymentRetry";
const STUDIO_PLUS_CHECKOUT_RETRY_FAILED_REASON = "failed";
const STUDIO_PLUS_CHECKOUT_RETRY_MESSAGE =
  "Payment was not completed. Please try another payment method.";

export function isStudioPlusPaymentsProvider(authStore: any) {
  return (
    authStore?.onlinePaymentProvider === STUDIO_PLUS_PAYMENTS_PROVIDER &&
    isValidAbsoluteHttpUrl(authStore?.studioPlusPaymentsWebURL) &&
    isValidAbsoluteHttpUrl(authStore?.studioPlusPaymentsApiURL)
  );
}

export function getStudioPlusPaymentsOrigin(webUrl?: string) {
  if (!webUrl) {
    return "";
  }

  try {
    return new URL(webUrl).origin;
  } catch {
    return "";
  }
}

export function getHostPageBackgroundColor(element?: Element | null) {
  if (typeof window === "undefined") {
    return "";
  }

  const visited = new Set<Element>();
  const candidates: Array<Element | null | undefined> = [
    element,
    element?.parentElement,
    document.body,
    document.documentElement,
  ];

  for (const candidate of candidates) {
    let current = candidate;

    while (current && !visited.has(current)) {
      visited.add(current);

      const hexColor = toHexColor(window.getComputedStyle(current).backgroundColor);
      if (hexColor) {
        return hexColor;
      }

      current = current.parentElement;
    }
  }

  return "";
}

export function buildStudioPlusPayUrl(
  webUrl: string,
  paymentSessionId: string,
  returnUrl?: string,
  backgroundColor?: string
) {
  let url = `${trimBaseUrl(webUrl)}/Pay?paymentSessionId=${encodeURIComponent(
    paymentSessionId
  )}`;
  if (returnUrl) {
    url += `&returnUrl=${encodeURIComponent(returnUrl)}`;
  }
  const normalizedBackgroundColor = toHexColor(backgroundColor);
  if (normalizedBackgroundColor) {
    url += `&backgroundColor=${encodeURIComponent(normalizedBackgroundColor)}`;
  }
  return url;
}

export function buildStudioPlusPaymentMethodsUrl(
  webUrl: string,
  referenceKey: string
) {
  return `${trimBaseUrl(webUrl)}/PaymentMethods?referenceKey=${encodeURIComponent(
    referenceKey
  )}`;
}

export function buildStudioPlusCheckoutRetryUrl(
  pathname = "/checkout/payment"
) {
  const separator = pathname.includes("?") ? "&" : "?";
  return `${pathname}${separator}${STUDIO_PLUS_CHECKOUT_RETRY_QUERY_PARAM}=${STUDIO_PLUS_CHECKOUT_RETRY_FAILED_REASON}`;
}

export function getStudioPlusCheckoutRetryMessage(search?: string) {
  if (!search) {
    return "";
  }

  const params = new URLSearchParams(
    search.startsWith("?") ? search.substring(1) : search
  );

  return params.get(STUDIO_PLUS_CHECKOUT_RETRY_QUERY_PARAM)?.toLowerCase() ===
    STUDIO_PLUS_CHECKOUT_RETRY_FAILED_REASON
    ? STUDIO_PLUS_CHECKOUT_RETRY_MESSAGE
    : "";
}

export function isStudioPlusPaymentsMessage(
  event: MessageEvent,
  webUrl: string,
  messageType: string
) {
  const expectedOrigin = getStudioPlusPaymentsOrigin(webUrl);
  return (
    !!expectedOrigin &&
    event.origin === expectedOrigin &&
    typeof event.data === "object" &&
    event.data?.type === messageType
  );
}

export function isStudioPlusPaymentsTerminalStatus(status?: string) {
  return !!status && TERMINAL_PAYMENT_STATUSES.includes(status.toLowerCase());
}

export function isStudioPlusPaymentsSuccessStatus(status?: string) {
  return !!status && status.toLowerCase() === "succeeded";
}

export async function createStudioPlusPaymentSession(invoiceKey: string) {
  const response: AxiosResponse = await axios({
    method: "POST",
    url: "/ibyStudioPlusPayments/payment-session",
    data: {
      InvoiceKey: invoiceKey,
    },
  });

  return response.data;
}

export async function getStudioPlusPaymentStatus(paymentSessionId: string) {
  const response: AxiosResponse = await axios({
    method: "GET",
    url: `/ibyStudioPlusPayments/payment-status/${paymentSessionId}`,
  });

  return response.data;
}

export async function pollStudioPlusPaymentStatus(
  paymentSessionId: string,
  maxAttempts = 20,
  delayMs = 1500
) {
  let attempt = 0;

  while (attempt < maxAttempts) {
    const statusResponse = await getStudioPlusPaymentStatus(paymentSessionId);
    if (isStudioPlusPaymentsTerminalStatus(statusResponse?.Status)) {
      return statusResponse;
    }

    attempt += 1;
    await delay(delayMs);
  }

  return getStudioPlusPaymentStatus(paymentSessionId);
}

export async function createStudioPlusPaymentMethodsSession(clientKey?: string) {
  const response: AxiosResponse = await axios({
    method: "POST",
    url: "/ibyStudioPlusPayments/payment-methods-session",
    data: {
      ClientKey: clientKey,
    },
  });

  return response.data;
}

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function trimBaseUrl(url: string) {
  return url.replace(/\/+$/, "");
}

function isValidAbsoluteHttpUrl(url?: string) {
  if (!url) {
    return false;
  }

  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
  } catch {
    return false;
  }
}

function toHexColor(colorValue?: string) {
  if (!colorValue) {
    return "";
  }

  const normalizedValue = colorValue.trim();

  if (/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(normalizedValue)) {
    return normalizedValue;
  }

  const rgbMatch = normalizedValue.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*([01]?\.?\d*))?\s*\)$/i
  );

  if (!rgbMatch) {
    return "";
  }

  const alphaValue = rgbMatch[4];
  if (alphaValue !== undefined && Number(alphaValue) === 0) {
    return "";
  }

  const [red, green, blue] = rgbMatch
    .slice(1, 4)
    .map((channel) => Number(channel))
    .map((channel) => Math.max(0, Math.min(255, channel)))
    .map((channel) => channel.toString(16).padStart(2, "0"));

  return `#${red}${green}${blue}`;
}
